import { useState, useEffect, useCallback, type ReactNode } from 'react'
import api from '../services/api'
import { AuthContext, type AuthUser } from './auth-context'

declare global {
    interface Window {
        __ENV__?: {
            VITE_API_URL?: string
        }
    }
}

const TOKEN_KEY = 'easy_market_token'
const GUEST_KEY = 'easy_market_guest'

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<AuthUser | null>(null)
    const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY))
    const [isGuest, setIsGuest] = useState(() => localStorage.getItem(GUEST_KEY) === 'true')
    const [loading, setLoading] = useState(true)

    const fetchUser = useCallback(async (jwt: string) => {
        try {
            const res = await api.get<AuthUser>('/api/auth/me', {
                headers: { Authorization: `Bearer ${jwt}` },
            })
            setUser(res.data)
            setToken(jwt)
            localStorage.setItem(TOKEN_KEY, jwt)
            localStorage.removeItem(GUEST_KEY)
            setIsGuest(false)
        } catch {
            // Token invalide ou expiré
            localStorage.removeItem(TOKEN_KEY)
            setToken(null)
            setUser(null)
        }
    }, [])

    useEffect(() => {
        const init = async () => {
            const savedToken = localStorage.getItem(TOKEN_KEY)
            const params = new URLSearchParams(window.location.search)
            const hasTokenParam = window.location.pathname === '/login/success' && params.has('token')

            if (savedToken) {
                await fetchUser(savedToken)
            }
            if (!hasTokenParam) {
                setLoading(false)
            }
        }
        init()
    }, [fetchUser])

    const loginWithGoogle = () => {
        const backendUrl = window.__ENV__?.VITE_API_URL || import.meta.env.VITE_API_URL || 'http://localhost:8000'
        window.location.href = `${backendUrl}/api/auth/google/login`
    }

    const continueAsGuest = () => {
        localStorage.setItem(GUEST_KEY, 'true')
        setIsGuest(true)
        setUser(null)
        setToken(null)
        localStorage.removeItem(TOKEN_KEY)
    }

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY)
        localStorage.removeItem(GUEST_KEY)
        setToken(null)
        setUser(null)
        setIsGuest(false)
    }

    const saveToken = useCallback(async (jwt: string) => {
        setLoading(true)
        await fetchUser(jwt)
        setLoading(false)
    }, [fetchUser])

    return (
        <AuthContext.Provider
            value={{
                user,
                token,
                isAuthenticated: !!user && !!token,
                isGuest,
                loading,
                loginWithGoogle,
                continueAsGuest,
                logout,
            }}
        >
            <AuthCallbackHandler saveToken={saveToken} />
            {children}
        </AuthContext.Provider>
    )
}

/** Invisible component that handles the OAuth callback token extraction */
function AuthCallbackHandler({ saveToken }: { saveToken: (t: string) => Promise<void> }) {
    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        const tokenParam = params.get('token')
        if (window.location.pathname === '/login/success' && tokenParam) {
            saveToken(tokenParam).then(() => {
                window.history.replaceState({}, '', '/app')
            })
        }
    }, [saveToken])
    return null
}
